import { Component, Injector, Input, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { CATO_TEMPLATE_TYPES, CONFIG_TEMPLATE_TYPES, MERAKI_CONFIG_TEMPLATE_TYPES } from '@core/constants';
import { FileUploadModalComponent } from '@core/components/file-upload-modal/file-upload-modal.component';
import { GenericListComponent } from '@core/globals/generic-list/generic-list.component';
import { WcmTableComponent } from '@core/globals/wcm-table/wcm-table.component';
import { omit } from '@core/helpers';
import { SignalsService } from '@core/services/signals.service';
import {WaycomHttpErrorResponse} from "@core/services/waycom-http-error-response";


@Component({
  selector: 'app-network-devices-list',
  templateUrl: './network-devices-list.component.html',
  styles: []
})
export class NetworkDevicesListComponent extends GenericListComponent {
  @ViewChild('wcmTable') public wcmTable: WcmTableComponent;
  @Input() public source = '';

  public typeOptions: any;
  public conductorStateOptions: { [key: string]: string};
  public loadingAction: boolean = false;

  constructor(
    public injector: Injector,
    private ngbModal: NgbModal,
    private signalsService: SignalsService,

  ) {
    super(injector);
    this.typeOptions = {...CATO_TEMPLATE_TYPES, ...CONFIG_TEMPLATE_TYPES, ...MERAKI_CONFIG_TEMPLATE_TYPES};
    this.conductorStateOptions = {'ok': 'OK', 'error': 'Erreur'};
    this.localDisabledButtons = {create: true, import: false};
    this.localDisabledColumns = {
      is_active: true, status: true, entity__parent__code_or_name: true,
      operator_line__offer__type: true,
      secondary_operator_line__ref_or_code: true
    };
    this.localFilters = { is_active: true };
  }

  public showImportCatoNetworkDevicesModal(): void {
    const modal = this.ngbModal.open(FileUploadModalComponent, {size: 'lg', backdrop: 'static'});
    modal.componentInstance.uploadUrl = this.apiProvitool.network_devices.cato_network_devices_from_xlsx_import_url();
    modal.componentInstance.acceptedFileTypes = ['.xlsx'];
    modal.componentInstance.jobName = 'Import des équipements réseau Cato';
  }

  public showImportNetworkDevicesModal(): void {
    const modal = this.ngbModal.open(FileUploadModalComponent, {size: 'lg', backdrop: 'static'});
    modal.componentInstance.uploadUrl = this.apiProvitool.network_devices.network_devices_from_xlsx_import_url();
    modal.componentInstance.acceptedFileTypes = ['.xlsx'];
    modal.componentInstance.jobName = 'Import des équipements réseau';
  }

  public showImportMerakiNetworkDevicesModal(): void {
    const modal = this.ngbModal.open(FileUploadModalComponent, {size: 'lg', backdrop: 'static'});
    modal.componentInstance.uploadUrl = this.apiProvitool.network_devices.meraki_network_devices_from_xlsx_import_url();
    modal.componentInstance.acceptedFileTypes = ['.xlsx'];
    modal.componentInstance.jobName = 'Import des équipements réseau Meraki';
  }

  public showImportMerakiMGNetworkDevicesModal(): void {
    const modal = this.ngbModal.open(FileUploadModalComponent, {size: 'lg', backdrop: 'static'});
    modal.componentInstance.uploadUrl = this.apiProvitool.network_devices.meraki_mg_network_devices_from_xlsx_import_url();
    modal.componentInstance.acceptedFileTypes = ['.xlsx'];
    modal.componentInstance.jobName = 'Import des équipements réseau Meraki MG';
  }

  public downloadTemplate(name: string): void {
    this.apiShiva.templates.detailByName(name)
      .then((res) => {
        const url = this.apiShiva.attachments.downloadUrl(res['last_attachment_id']);
        window.open(url, 'Téléchargement du template');
      }, err => {
        console.error('Erreur lors du téléchargement du template', err);
        this.toastr.error('Erreur lors du téléchargement du template');
      });
  }

  public filteredExport(tableFilters): void {
    const filters = omit(tableFilters, 'limit', 'offset', 'ordering', 'serializer');
    const taskName = 'Export des ips wan.';

    this.apiProvitool.network_devices.export(filters)
      .then((res) => {
        // When the server response is received and it's a success,
        this.signalsService.broadcastJobStart(taskName, res.job_id);
        this.toastr.success('Demande prise en compte. Veuillez patienter le temps que le fichier d\'export soit généré.');
      }).catch((err) => {
        this.toastr.error('Echec de la demande d\'export. Veuillez réessayer.');
      });
  }

  // ---------------------------------------------------------
  // Bulk action (for selected items)
  // ---------------------------------------------------------

  public clearSelection(): void {
    this.wcmTable.unselectAll();
  }

  private _refreshTable(): void {
    this.wcmTable.refreshTable();
  }

  public bulkReplaySynchro(selectedIds: {}): void{
    this.loadingAction = true;
    this.apiProvitool.network_devices.bulk_replay_synchro(selectedIds).then((res: unknown) => {
      this.signalsService.broadcastJobStart('Replay synchro workflow', res['job_id']);
      this.clearSelection();
      this._refreshTable();
      this.toastr.success('Demande prise en compte. Veuillez patienter le temps que le workflow se rejoue.');
    })
      .catch((err) => {
        console.error('Erreur lors du rejeu du workflow', err);
        if(err instanceof WaycomHttpErrorResponse){
          if (err.getFirstErrorMessage() === 'INVALID_NETWORK_DEVICE_TYPE'){
            this.toastr.error('Seuls les devices Cato peuvent être rejoués.');
          }
          if (err.getFirstErrorMessage() === 'INVALID_NETWORK_DEVICE_STATE'){
            this.toastr.error('Seuls les devices en erreur ou en service peuvent être rejoués.');
          }
        }
        return Promise.reject(err);
      })
      .finally(() => this.loadingAction = false);
  }

  public bulkDeleteCato(selectedIds: {}): void {
    this.loadingAction = true;
    this.apiProvitool.network_devices.bulk_delete_nd_cato(selectedIds)
      .then((res: unknown) => {
      this.signalsService.broadcastJobStart('Bulk delete nd Cato', res['job_id']);
      this.clearSelection();
      this._refreshTable();
      this.toastr.success('Demande de suppression prise en compte. Veuillez patienter le temps que Conductor travaille.');
    })
      .catch((err) => {
        console.error('Erreur lors de la demande de suppression', err);
        if(err instanceof WaycomHttpErrorResponse){
          if (err.getFirstErrorMessage() === 'INVALID_NETWORK_DEVICE_TYPE'){
            this.toastr.error('Seuls les devices Cato peuvent être supprimés en masse.');
          }
        }
        return Promise.reject(err);
      })
      .finally(() => this.loadingAction = false);
  }
}
